(() => {
  'use strict';

  if (window.CCSWCommerce) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SELECTORS = {
    mainProduct: '[data-ccsw-main-product]',
    gallery: '[data-ccsw-gallery]',
    mediaThumb: '[data-ccsw-media-thumb]',
    mediaPanel: '[data-ccsw-media-panel]',
    optionInput: '[data-ccsw-option-input]',
    optionGroup: '[data-ccsw-option-group]',
    ajaxForm: '.ccsw-ajax-product-form',
    formStatus: '.ccsw-form-status',
    recommendations: '[data-ccsw-recommendations]',
    collection: '[data-ccsw-collection]',
    filterDrawer: '[data-ccsw-filter-drawer]',
    filterOverlay: '[data-ccsw-filter-overlay]'
  };

  const state = {
    productRequest: null
  };

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const setFormMessage = (form, message, isError = false) => {
    const status = qs(SELECTORS.formStatus, form);
    if (!status) return;
    status.textContent = message;
    status.style.color = isError ? '#b42318' : '';
  };

  const updateHeaderCart = async () => {
    try {
      const response = await fetch(`${ROOT}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return;
      const cart = await response.json();

      qsa('[data-ccsw-cart-count]').forEach((element) => {
        element.textContent = cart.item_count;
        element.hidden = cart.item_count === 0;
      });

      qsa('[data-ccsw-cart-total]').forEach((element) => {
        element.textContent = formatMoney(cart.total_price, cart.currency);
      });
    } catch (_error) {
      // A cart refresh is an enhancement. A normal page refresh will recover state.
    }
  };

  const handleAjaxAdd = async (form) => {
    const button = qs('button[type="submit"]', form);
    const originalLabel = button ? button.innerHTML : '';

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    setFormMessage(form, 'Adding to cart…');

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form),
        credentials: 'same-origin'
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'This item could not be added to the cart.');
      }

      setFormMessage(form, 'Added to cart.');
      await updateHeaderCart();
      document.dispatchEvent(new CustomEvent('ccsw:cart:updated', { detail: payload }));
    } catch (error) {
      setFormMessage(form, error.message || 'This item could not be added to the cart.', true);
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = originalLabel;
      }
    }
  };

  const activateMedia = (button) => {
    const gallery = button.closest(SELECTORS.gallery);
    if (!gallery) return;
    const mediaId = button.dataset.mediaId;

    qsa(SELECTORS.mediaThumb, gallery).forEach((thumb) => {
      const active = thumb === button;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    qsa(SELECTORS.mediaPanel, gallery).forEach((panel) => {
      const active = panel.dataset.mediaId === mediaId;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
      if (!active) {
        qsa('video', panel).forEach((video) => video.pause());
      }
    });
  };

  const getSelectedOptionState = (productRoot) => {
    const ids = [];
    let productUrl = productRoot.dataset.productUrl || window.location.pathname;

    qsa(SELECTORS.optionGroup, productRoot).forEach((group) => {
      const select = qs('select[data-ccsw-option-input]', group);
      if (select) {
        const option = select.selectedOptions[0];
        if (option && option.dataset.optionValueId) ids.push(option.dataset.optionValueId);
        if (option && option.dataset.productUrl) productUrl = option.dataset.productUrl;
        return;
      }

      const checked = qs('input[data-ccsw-option-input]:checked', group);
      if (checked && checked.dataset.optionValueId) ids.push(checked.dataset.optionValueId);
      if (checked && checked.dataset.productUrl) productUrl = checked.dataset.productUrl;
    });

    return { ids, productUrl };
  };

  const updateProductSection = async (input) => {
    const productRoot = input.closest(SELECTORS.mainProduct);
    if (!productRoot) return;

    const sectionId = productRoot.dataset.sectionId;
    const { ids, productUrl } = getSelectedOptionState(productRoot);
    if (!sectionId || !ids.length) return;

    if (state.productRequest) state.productRequest.abort();
    state.productRequest = new AbortController();

    productRoot.classList.add('is-loading');
    productRoot.setAttribute('aria-busy', 'true');

    try {
      const requestUrl = new URL(productUrl || productRoot.dataset.productUrl, window.location.origin);
      requestUrl.searchParams.delete('variant');
      requestUrl.searchParams.set('section_id', sectionId);
      requestUrl.searchParams.set('option_values', ids.join(','));

      const response = await fetch(requestUrl.toString(), {
        signal: state.productRequest.signal,
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Unable to update product options.');

      const responseText = await response.text();
      const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
      const replacement = qs(SELECTORS.mainProduct, documentFragment);
      if (!replacement) throw new Error('Updated product content was not returned.');

      productRoot.replaceWith(replacement);

      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        window.Shopify.PaymentButton.init();
      }

      const selectedVariantInput = qs('[data-ccsw-variant-id]', replacement);
      const displayUrl = new URL(productUrl || replacement.dataset.productUrl, window.location.origin);
      displayUrl.searchParams.delete('section_id');
      displayUrl.searchParams.delete('option_values');
      displayUrl.searchParams.delete('variant');

      if (selectedVariantInput && selectedVariantInput.value) {
        displayUrl.searchParams.set('variant', selectedVariantInput.value);
      } else {
        displayUrl.searchParams.set('option_values', ids.join(','));
      }

      window.history.replaceState({}, '', `${displayUrl.pathname}${displayUrl.search}${displayUrl.hash}`);
      document.dispatchEvent(new CustomEvent('ccsw:product:updated', { detail: { sectionId } }));
    } catch (error) {
      if (error.name === 'AbortError') return;

      const fallback = new URL(productUrl || productRoot.dataset.productUrl, window.location.origin);
      fallback.searchParams.set('option_values', ids.join(','));
      window.location.assign(fallback.toString());
    } finally {
      const current = qs(`[data-ccsw-main-product][data-section-id="${CSS.escape(sectionId)}"]`);
      if (current) {
        current.classList.remove('is-loading');
        current.removeAttribute('aria-busy');
      }
    }
  };

  const openZoom = (button) => {
    const productRoot = button.closest(SELECTORS.mainProduct);
    const dialog = productRoot && qs('[data-ccsw-zoom-dialog]', productRoot);
    const image = dialog && qs('[data-ccsw-zoom-image]', dialog);
    if (!dialog || !image) return;

    image.src = button.dataset.imageUrl || '';
    image.alt = button.dataset.imageAlt || '';

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      window.open(image.src, '_blank', 'noopener');
    }
  };

  const closeZoom = (button) => {
    const dialog = button.closest('dialog');
    if (dialog && typeof dialog.close === 'function') dialog.close();
  };

  const loadRecommendations = (scope = document) => {
    qsa(`${SELECTORS.recommendations}:not([data-ccsw-loaded])`, scope).forEach(async (element) => {
      const url = element.dataset.url;
      if (!url) return;

      element.dataset.ccswLoaded = 'true';
      element.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Unable to load recommendations.');
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const replacement = qs(SELECTORS.recommendations, parsed);
        if (replacement && replacement.innerHTML.trim()) {
          element.innerHTML = replacement.innerHTML;
        } else {
          const section = element.closest('.ccsw-product-recommendations-section');
          if (section) section.hidden = true;
        }
      } catch (_error) {
        const section = element.closest('.ccsw-product-recommendations-section');
        if (section) section.hidden = true;
      } finally {
        element.removeAttribute('aria-busy');
      }
    });
  };

  const setFilterDrawer = (collectionRoot, open) => {
    const drawer = qs(SELECTORS.filterDrawer, collectionRoot);
    const overlay = qs(SELECTORS.filterOverlay, collectionRoot);
    const trigger = qs('[data-ccsw-filter-open]', collectionRoot);
    if (!drawer) return;

    drawer.classList.toggle('is-open', open);
    if (overlay) overlay.classList.toggle('is-open', open);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('ccsw-filter-open', open);
  };

  const applySort = (select) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const applyGridView = (collectionRoot, view) => {
    const grid = qs('[data-ccsw-product-grid]', collectionRoot);
    if (!grid) return;
    const listView = view === 'list';
    grid.classList.toggle('is-list', listView);

    qsa('[data-ccsw-view]', collectionRoot).forEach((button) => {
      const active = button.dataset.ccswView === view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    try {
      window.localStorage.setItem('ccsw-product-view', view);
    } catch (_error) {
      // Local storage is optional.
    }
  };

  const restoreGridView = (scope = document) => {
    let view = 'grid';
    try {
      view = window.localStorage.getItem('ccsw-product-view') || 'grid';
    } catch (_error) {
      view = 'grid';
    }
    qsa(SELECTORS.collection, scope).forEach((root) => applyGridView(root, view));
  };

  document.addEventListener('click', (event) => {
    const mediaButton = event.target.closest(SELECTORS.mediaThumb);
    if (mediaButton) {
      event.preventDefault();
      activateMedia(mediaButton);
      return;
    }

    const zoomButton = event.target.closest('[data-ccsw-zoom]');
    if (zoomButton) {
      event.preventDefault();
      openZoom(zoomButton);
      return;
    }

    const zoomClose = event.target.closest('[data-ccsw-zoom-close]');
    if (zoomClose) {
      event.preventDefault();
      closeZoom(zoomClose);
      return;
    }

    const quantityMinus = event.target.closest('[data-ccsw-quantity-minus]');
    if (quantityMinus) {
      const control = quantityMinus.closest('.ccsw-quantity__control');
      const input = control && qs('[data-ccsw-quantity-input]', control);
      if (input) {
        const minimum = Number(input.min || 1);
        input.value = Math.max(minimum, Number(input.value || minimum) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const quantityPlus = event.target.closest('[data-ccsw-quantity-plus]');
    if (quantityPlus) {
      const control = quantityPlus.closest('.ccsw-quantity__control');
      const input = control && qs('[data-ccsw-quantity-input]', control);
      if (input) {
        const maximum = input.max ? Number(input.max) : Infinity;
        input.value = Math.min(maximum, Number(input.value || 1) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const filterOpen = event.target.closest('[data-ccsw-filter-open]');
    if (filterOpen) {
      const root = filterOpen.closest(SELECTORS.collection);
      if (root) setFilterDrawer(root, true);
      return;
    }

    const filterClose = event.target.closest('[data-ccsw-filter-close]');
    if (filterClose) {
      const root = filterClose.closest(SELECTORS.collection);
      if (root) setFilterDrawer(root, false);
      return;
    }

    const filterOverlay = event.target.closest(SELECTORS.filterOverlay);
    if (filterOverlay) {
      const root = filterOverlay.closest(SELECTORS.collection);
      if (root) setFilterDrawer(root, false);
      return;
    }

    const viewButton = event.target.closest('[data-ccsw-view]');
    if (viewButton) {
      const root = viewButton.closest(SELECTORS.collection);
      if (root) applyGridView(root, viewButton.dataset.ccswView || 'grid');
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches(SELECTORS.optionInput)) {
      updateProductSection(event.target);
      return;
    }

    if (event.target.matches('[data-ccsw-sort]')) {
      applySort(event.target);
      return;
    }

    if (event.target.matches('[data-ccsw-filter-input]')) {
      const form = event.target.closest('[data-ccsw-facets-form]');
      if (!form) return;
      window.clearTimeout(form._ccswFilterTimer);
      form._ccswFilterTimer = window.setTimeout(() => form.requestSubmit(), 250);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(SELECTORS.ajaxForm);
    if (!form) return;

    // Allow Shopify's accelerated checkout buttons to submit normally.
    // Only the CCSW add-to-cart button (or an Enter-key submission) uses AJAX.
    if (event.submitter && !event.submitter.matches('[data-ccsw-ajax-submit]')) return;

    event.preventDefault();
    handleAjaxAdd(form);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    qsa(`${SELECTORS.collection} ${SELECTORS.filterDrawer}.is-open`).forEach((drawer) => {
      const root = drawer.closest(SELECTORS.collection);
      if (root) setFilterDrawer(root, false);
    });
    qsa('[data-ccsw-zoom-dialog][open]').forEach((dialog) => dialog.close());
  });

  document.addEventListener('DOMContentLoaded', () => {
    loadRecommendations(document);
    restoreGridView(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    loadRecommendations(event.target);
    restoreGridView(event.target);
  });

  window.CCSWCommerce = {
    loadRecommendations,
    restoreGridView,
    updateHeaderCart
  };
})();
